import { cn } from '~/utils/cn';
import { formatDateShort, modalityLabel } from '~/utils/contractLabels';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';
import type { Contract, ContractInstallment } from '~/store/contracts/interfaces';

import BankAvatar from '../../atoms/BankAvatar/view';
import { Icon } from '../../atoms/Icon/view';
import ProgressBar from '../../atoms/ProgressBar/view';
import RiskChip from '../../atoms/RiskChip/view';
import { Text } from '../../atoms/Text/view';

interface PropsContractReportRow {
  contract: Contract;
  nextInstallment?: ContractInstallment;
  overdueCount?: number;
  onClick?: () => void;
  onExport?: () => void;
  className?: string;
}

export default function ContractReportRow({
  contract,
  nextInstallment,
  overdueCount = 0,
  onClick,
  onExport,
  className,
}: PropsContractReportRow) {
  const percent =
    contract.amountTotal > 0
      ? Math.round((contract.amountPaid / contract.amountTotal) * 100)
      : 0;

  return (
    <div
      className={cn(
        'group relative w-full rounded-medium bg-intense-01 border border-intense-03 px-small py-smaller shadow-level1 hover:shadow-level2 transition-shadow',
        className,
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left flex flex-col gap-smaller"
      >
        <div className="flex items-start gap-smaller">
          <BankAvatar institution={contract.institution} size="md" />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-quark flex-wrap">
              <Text className="text-paragraphLG color-complement-01" numberOfLines={1}>
                {contract.institution.name}
              </Text>
              {overdueCount > 0 ? (
                <span className="px-quark py-[2px] rounded-full bg-warning-01 border border-warning-02">
                  <Text className="text-captionMD color-warning-06 font-semibold">
                    {overdueCount} vencida{overdueCount > 1 ? 's' : ''}
                  </Text>
                </span>
              ) : null}
            </div>
            <Text className="text-captionMD color-intense-06 mt-quark" numberOfLines={1}>
              {modalityLabel(contract.modality)} · #{contract.contractNumber}
            </Text>
          </div>

          <div className="flex flex-col items-end shrink-0">
            <Text className="text-paragraphLG color-complement-01">
              {formatCentsToBRLCompact(contract.amountRemaining)}
            </Text>
            <Text className="text-captionMD color-intense-06">restante</Text>
          </div>
        </div>

        <div className="flex items-center justify-between gap-quark min-w-0">
          <Text className="text-captionLG color-primary-01 font-semibold shrink-0">
            {percent}% quitado
          </Text>
          {nextInstallment ? (
            <div className="flex items-center gap-quark min-w-0">
              <Text className="text-captionMD color-intense-06 shrink-0">Próxima:</Text>
              <Text className="text-bodyMD color-complement-01 shrink-0">
                {formatCentsToBRLCompact(nextInstallment.amount)} ·{' '}
                {formatDateShort(nextInstallment.dueDate)}
              </Text>
            </div>
          ) : (
            <Text className="text-captionMD color-success-06 shrink-0">Sem parcelas pendentes</Text>
          )}
        </div>

        <ProgressBar value={percent} max={100} trackClassName="h-[5px]" />

        <div className="flex items-center justify-between gap-quark">
          <RiskChip level={contract.riskLevel} compact />
          <Text className="text-captionMD color-intense-06">
            {contract.installmentsPaid}/{contract.installmentsTotal} parcelas
          </Text>
        </div>
      </button>

      {onExport ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExport();
          }}
          aria-label="Gerar relatório deste contrato"
          className="absolute top-smaller right-smaller p-quark rounded-small hover:bg-intense-03 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Icon name="icon-extract" size={16} color="#1D4733" rounded={false} />
        </button>
      ) : null}
    </div>
  );
}
